import React, { useCallback } from "react";
import { createPortal } from "react-dom";
import { FirebaseFirestoreTypes } from "@react-native-firebase/firestore";
import { Box, Stack, Text } from "native-base";
import { DndContext, DragOverlay, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { AreaLevelLookup } from "../../../hooks/useAreas";
import { ObjectType } from "../../../hooks/useObjectType";
import { useAreaDragAndDrop } from "../../../hooks/useAreaDragAndDrop";
import { useMoveArea } from "../../../hooks/useMoveArea.web";
import { useAreaExpansion } from "./AreaExpansionContext";
import { AreaDoc, INDENT_WIDTH } from "../../../helper/area-tree";
import AddAreaButton from "./AddAreaButton.web";
import DisplayArea from "./DisplayArea.web";

type AreaSnapshot =
  FirebaseFirestoreTypes.QueryDocumentSnapshot<FirebaseFirestoreTypes.DocumentData>;

type Props = {
  objectType: ObjectType | null;
  projectId: string;
  objectId: string;
  areas: AreaSnapshot[];
  allAreas: AreaSnapshot[];
  levelForAreaId: AreaLevelLookup;
  parentId: string | null;
  addArea: (areaName: string, parentId: string | null) => void;
  cracksByAreaId: Map<string, AreaSnapshot[]>;
  isLoadingCracks: boolean;
  rootAreaType?: string;
};

/**
 * Web renders the whole area tree as one flat, single-context sortable list.
 * The native build keeps the recursive DisplayAreas: only web supports dragging,
 * and the flat shape exists purely so a drag can express a new parent.
 */
export default function DisplayAreas({
  objectType,
  projectId,
  objectId,
  allAreas,
  addArea,
  cracksByAreaId,
  isLoadingCracks,
}: Props) {
  const { isExpanded } = useAreaExpansion();

  const moveArea = useMoveArea(allAreas as any);

  const hasCracks = useCallback(
    (areaId: string) => (cracksByAreaId.get(areaId)?.length ?? 0) > 0,
    [cracksByAreaId]
  );

  const {
    sensors,
    rows,
    sortableIds,
    activeId,
    activeRow,
    projection,
    handleDragStart,
    handleDragMove,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
  } = useAreaDragAndDrop({
    areas: allAreas as unknown as AreaDoc[],
    objectType,
    isExpanded,
    hasCracks,
    moveArea,
  });

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
        <Stack>
          {rows.map((row) =>
            row.kind === "area" ? (
              <DisplayArea
                key={row.id}
                row={row}
                projectId={projectId}
                objectId={objectId}
                objectType={objectType}
                cracksCount={cracksByAreaId.get(row.id)?.length ?? 0}
                isLoadingCracks={isLoadingCracks}
                // While this row is the one being dragged, it is drawn at the
                // level it would land on, so the indent shows the new parent.
                indentLevel={
                  row.id === activeId && projection ? projection.level : row.level
                }
              />
            ) : (
              <Box
                key={row.id}
                mb={4}
                mt={2}
                style={{ paddingLeft: (row.level - 1) * INDENT_WIDTH }}
              >
                <AddAreaButton
                  onAddAreaWithName={(areaName) =>
                    areaName && addArea(areaName, row.parentId)
                  }
                  level={row.level}
                  rootAreaType={row.rootAreaType}
                  objectType={objectType}
                />
              </Box>
            )
          )}
        </Stack>
      </SortableContext>

      {createPortal(
        <DragOverlay>
          {activeRow && (
            <Box w="full" h="12" p="4" bgColor="white" shadow="9" opacity="1">
              <Text fontSize="md" color="moss.600">
                {activeRow.area.get("title") as string}
              </Text>
            </Box>
          )}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
}
